import { Box, Grid, Stack } from '@mui/joy';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import 'reports/config';
import { SERVER_FORMAT_DATE } from 'core/constants';
import { formatDate } from 'core/utils';
import { ITssDashboardFilters } from 'reports/components';

import {
  JobsByEngineerChart,
  JobsByPartChart,
  JobsByTheatreChart,
  LinesByPartChart,
  TrendingCustomersChart,
  TrendingJobsChart,
} from './charts';
import { DashboardSummary } from './DashboardSummary';
import { Filter } from './Filter';

const CHART_HEIGHT = 300;

export const TssDashboard = () => {
  const [filters, setFilters] = useState<ITssDashboardFilters>({});

  const { t } = useTranslation();

  const {
    startDate,
    endDate,
    currentYearStart,
    currentYearEnd,
    previousYearStart,
    previousYearEnd,
  } = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const start = new Date(today);
    start.setFullYear(start.getFullYear() - 1);

    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentDay = now.getDate();

    return {
      startDate: formatDate(start, false, SERVER_FORMAT_DATE),
      endDate: formatDate(today, false, SERVER_FORMAT_DATE),
      currentYearStart: formatDate(new Date(currentYear, 0, 1), false, SERVER_FORMAT_DATE),
      currentYearEnd: formatDate(now, false, SERVER_FORMAT_DATE),
      previousYearStart: formatDate(new Date(currentYear - 1, 0, 1), false, SERVER_FORMAT_DATE),
      previousYearEnd: formatDate(
        new Date(currentYear - 1, currentMonth, currentDay),
        false,
        SERVER_FORMAT_DATE
      ),
    };
  }, []);

  return (
    <Stack gap={1}>
      <Box sx={{ paddingX: 1 }}>
        <Filter onFiltersChange={setFilters} />
      </Box>

      <DashboardSummary filters={filters} />

      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <TrendingJobsChart
            title={t('labelTrendingCases')}
            startDate={startDate}
            endDate={endDate}
            filters={filters}
            height={CHART_HEIGHT}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <TrendingCustomersChart
            title={t('labelTrendingCustomersInteractions')}
            startDate={startDate}
            endDate={endDate}
            filters={filters}
            height={CHART_HEIGHT}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <JobsByEngineerChart
            title={t('labelCasesByEngineer')}
            currentYearStart={currentYearStart}
            currentYearEnd={currentYearEnd}
            previousYearStart={previousYearStart}
            previousYearEnd={previousYearEnd}
            filters={filters}
            height={CHART_HEIGHT}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <LinesByPartChart
            title={t('labelLinesByPart')}
            startDate={startDate}
            endDate={endDate}
            filters={filters}
            height={CHART_HEIGHT}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <JobsByPartChart
            title={t('labelCasesByPart')}
            currentYearStart={currentYearStart}
            currentYearEnd={currentYearEnd}
            previousYearStart={previousYearStart}
            previousYearEnd={previousYearEnd}
            filters={filters}
            height={CHART_HEIGHT}
          />
        </Grid>
        <Grid xs={12} md={6}>
          <JobsByTheatreChart
            title={t('labelCasesByTheater')}
            currentYearStart={currentYearStart}
            currentYearEnd={currentYearEnd}
            previousYearStart={previousYearStart}
            previousYearEnd={previousYearEnd}
            filters={filters}
            height={CHART_HEIGHT}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
